import Link from "next/link"

interface CTABandProps {
  /** Large black headline on the left */
  title: string
  /** Supporting body paragraph on the right (omit to render title + button only) */
  body?: string
  /** CTA button label */
  buttonLabel: string
  /** CTA button link destination */
  buttonHref: string
  /** Optional orange eyebrow above the title (default: "Next step") */
  eyebrow?: string
  /** Additional classes on the outer section */
  className?: string
}

/**
 * CTABand — LIGHT warm CTA block (owner rule: light backgrounds only) with a
 * diffused orange radial glow in the top-right corner, sitting in the rounded
 * 2xl container that closes every tool-page variant.
 *
 * Distinct from `CTABanner`, which uses a lifestyle-image background with
 * a dark overlay. Use `CTABand` inside tool pages for a more editorial,
 * flat treatment that matches the /tools index voice.
 */
export function CTABand({
  title,
  body,
  buttonLabel,
  buttonHref,
  eyebrow = "Next step",
  className = "",
}: CTABandProps) {
  return (
    <section className={`bg-white border-t border-gray-200 py-8 md:py-12 ${className}`}>
      <div className="max-w-[1320px] mx-auto px-6 md:px-8">
        <div className="rounded-2xl p-6 sm:p-8 md:p-10 relative overflow-hidden bg-[#fff7f0] border border-[#ffd9b8] text-gray-900">
          {/* Diffused orange glow */}
          <div
            aria-hidden
            className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
            style={{
              background: "rgba(255,153,81,0.25)",
              filter: "blur(70px)",
              transform: "translate(30%, -40%)",
            }}
          />
          <div className="relative grid md:grid-cols-[1.2fr_1fr] gap-6 md:gap-8 items-center md:items-end">
            <div>
              {/* FRIDGE-1591: #a65217 reads at 4.6:1 or more on the band, the glow behind it included. */}
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-2 text-[#a65217]">
                {eyebrow}
              </div>
              <h2
                className="font-black"
                style={{
                  fontSize: "clamp(26px, 3vw, 40px)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                  textWrap: "balance" as never,
                }}
              >
                {title}
              </h2>
            </div>
            <div className="flex flex-col items-start md:items-end gap-4">
              {body && (
                <p className="text-[14px] sm:text-[15px] text-gray-600 leading-[1.5] max-w-md md:text-right">
                  {body}
                </p>
              )}
              <Link
                href={buttonHref}
                className="inline-flex items-center gap-2 font-bold text-[var(--color-brand-action-text)] px-6 py-3 rounded-full bg-[#ff9951] hover:bg-[#f08030] transition-colors text-[13px] sm:text-[14px]"
              >
                {buttonLabel}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  width="16"
                  height="16"
                >
                  <path d="M7 17 17 7M7 7h10v10" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
